import * as React from "react";

import { cn } from "@/lib/utils";

export interface BentoItem {
  /** Card heading. */
  title: string;
  /** Optional muted text shown next to the title (e.g. a currency or tag). */
  meta?: string;
  /** Optional leading icon (lucide). Rendered in a rounded badge. */
  icon?: React.ReactNode;
  /** Optional one-line description (marketing-style; usually unused for finance cards). */
  description?: string;
  /** Optional status pill text (top-right). */
  status?: string;
  /** Optional hashtags row (usually unused for finance cards). */
  tags?: string[];
  /** Optional CTA text revealed on hover (usually unused for finance cards). */
  cta?: string;
  /** Span two columns on md+ (the "wide" bento tile). */
  colSpan?: 1 | 2;
  /** Financial body — line-items / tables rendered inside the tile. */
  children?: React.ReactNode;
  /** Passed through for test hooks. */
  "data-testid"?: string;
}

interface BentoGridProps {
  items: BentoItem[];
  className?: string;
}

function BentoGrid({ items, className }: BentoGridProps) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3",
        className,
      )}
    >
      {items.map((item, index) => (
        <div
          key={index}
          data-testid={item["data-testid"]}
          style={{
            transition:
              "transform .5s cubic-bezier(.22,.61,.36,1), border-color .5s ease, box-shadow .5s ease",
          }}
          className={cn(
            "group relative overflow-hidden rounded-xl p-4 will-change-transform",
            "border bg-card border-border dark:border-white/[0.08]",
            "hover:-translate-y-[3px] hover:scale-[1.012]",
            "hover:shadow-[0_12px_38px_-14px_rgba(0,0,0,0.72)]",
            "dark:hover:border-white/[0.10]",
            "motion-reduce:transform-none motion-reduce:transition-none",
            item.colSpan === 2 ? "md:col-span-2" : "col-span-1",
          )}
        >
          {/* Fixed diagonal wash: bright top-left, dark opposite corners */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-[600ms] ease-out group-hover:opacity-100 motion-reduce:transition-none"
            style={{
              background:
                "radial-gradient(140% 140% at 0% 0%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.045) 25%, rgba(255,255,255,0.012) 45%, transparent 68%)",
            }}
          />
          {/* Fixed diagonal edge sheen (1px ring via mask) */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-xl p-px opacity-0 transition-opacity duration-[600ms] ease-out group-hover:opacity-100 motion-reduce:transition-none"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.06) 30%, transparent 60%)",
              WebkitMask:
                "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
            }}
          />

          <div className="relative z-[1] flex flex-col space-y-3">
            {(item.icon || item.status) && (
              <div className="flex items-center justify-between">
                {item.icon && (
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-info transition-colors duration-300">
                    {item.icon}
                  </div>
                )}
                {item.status && (
                  <span className="rounded-lg bg-muted px-2 py-1 text-xs font-medium text-muted-foreground backdrop-blur-sm transition-colors duration-300">
                    {item.status}
                  </span>
                )}
              </div>
            )}

            <div className="space-y-2">
              <h3 className="text-[15px] font-semibold tracking-tight text-foreground">
                {item.title}
                {item.meta && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {item.meta}
                  </span>
                )}
              </h3>
              {item.description && (
                <p className="text-sm leading-snug text-muted-foreground">
                  {item.description}
                </p>
              )}
            </div>

            {/* Financial body */}
            {item.children}

            {(item.tags?.length || item.cta) && (
              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {item.tags?.map((tag, i) => (
                    <span
                      key={i}
                      className="rounded-md bg-muted px-2 py-1 backdrop-blur-sm transition-all duration-200"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
                {item.cta && (
                  <span className="text-xs text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
                    {item.cta}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export { BentoGrid };
